// ABOUTME: Blocking pre-entry consent modal shown before the microphone can start.
// ABOUTME: Presents one consolidated disclosure and two equal actions: accept all / reject all (no per-category toggles here).

import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CATEGORIES, DISCLOSURE } from './consentConfig';
import {
  RAIL_UNIT, u, CONSENT_PANEL, CONSENT_TEXT, PANEL_RADIUS, PANEL_PAD, PANEL_W, PANEL_MAX_VW,
} from '../components/ReelChrome/railGeometry';
import {
  MOBILE_UNIT, MOBILE_CONSENT, MOBILE_CONSENT_TEXT, MOBILE_PANEL, MIN_TEXT_PX,
} from '../components/ReelChrome/mobileGeometry';
import { useBreakpoint } from '../components/Projectionist/useViewportSize';

// Reject = necessary on, every optional category off. GDPR: refusing must be one click — and so, since 2026-10-08 (owner), is
// accepting: the gate carries no per-category toggles, the in-app Privacy & consent panel keeps them for a granular change.
const rejectAll = () =>
  CATEGORIES.reduce((acc, c) => {
    acc[c.id] = c.locked ? true : false;
    return acc;
  }, {});
const acceptAll = () =>
  CATEGORIES.reduce((acc, c) => {
    acc[c.id] = true;
    return acc;
  }, {});

/**
 * `reelChrome` swaps the generic white-modal look for the dark reel-chrome panel (Figma 113:6517)
 * without changing the blocking-gate behavior at all: still no Escape/backdrop dismissal, still
 * the same two equal-weight actions. Since 2026-10-08 the gate has no category rows at all: one
 * consolidated disclosure and accept-all / reject-all. The per-category rows (`ConsentCategoryList`)
 * live on in the in-app ConsentPanel / MenuConsentPanel, where a guest makes a granular change.
 */
export function ConsentGate({ open, onSave, onOpenPrivacy, reelChrome = false }) {
  const isMobile = useBreakpoint() === 'mobile';

  // Deliberately NOT dismissible: no Escape handler and no backdrop-click close. This is a
  // blocking opt-in gate; the guest must make an explicit Accept/Reject choice (GDPR), so the
  // only exits are the two action buttons below.
  if (reelChrome) {
    // Same geometry/unit switch the rail's cart, wishlist and privacy panels already make
    // (IconRail.js / MobileChrome.js / ConsentPanel.js) — this gate never adopted it and instead
    // always rendered the desktop CONSENT_PANEL/CONSENT_TEXT table under RAIL_UNIT, whose floor
    // (0.43px) is a fixed real-px size on every viewport below ~2064px, i.e. every phone. `width`
    // was also a bare Tailwind `max-w-2xl`, disconnected from that same unit system and from the
    // rail panels' own `panelWidth()`/`MOBILE_PANEL.w`. Reported 2026-09-01: on a phone the footer
    // labels ("Reject optional & enter") wrapped to two lines inside a fixed-`height` pill and the
    // wrapped line clipped past the button's own rounded ends.
    const unitCss = isMobile ? MOBILE_UNIT : RAIL_UNIT;
    const geometry = isMobile ? MOBILE_CONSENT : CONSENT_PANEL;
    const text = isMobile ? MOBILE_CONSENT_TEXT : CONSENT_TEXT;
    const panelPad = isMobile ? MOBILE_PANEL.pad : PANEL_PAD;
    const panelRadius = isMobile ? MOBILE_PANEL.radius : PANEL_RADIUS;
    // Matches the rail's cart/wishlist/privacy panels exactly (same PANEL_W/MOBILE_PANEL.w this
    // gate's own category rows already share via `geometry`), so the pre-entry gate is the same
    // width as every other rail panel instead of an unrelated Tailwind breakpoint.
    const panelWidthCss = isMobile ? u(MOBILE_PANEL.w) : `min(${u(PANEL_W)}, ${PANEL_MAX_VW}vw)`;
    // Real-px floor on the type, proportional to the row body (same rule as PrivacyPolicy.js and
    // the rail's ConsentPanel). This is the FIRST screen a phone guest reads and it was the one
    // consent surface with no floor: 10.8px body at 390px, 8.8px at 320px (2026-09-03). At the
    // desktop unit's floor 26.667 sets at 11.47, so a laptop is unchanged.
    const type = (authored) => `max(${((MIN_TEXT_PX * authored) / text.rowBody).toFixed(3)}px, ${u(authored)})`;

    return (
      <AnimatePresence>
        {open && (
          <motion.div
            key="consent-gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-label={DISCLOSURE.title}
            // Safe-area insets on the backdrop, so the panel's percentage max-height below already
            // has the notch and home indicator subtracted — the same arrangement PrivacyPolicy uses.
            style={{
              '--u': unitCss,
              paddingTop: 'calc(1rem + var(--reel-safe-t))',
              paddingBottom: 'calc(1rem + var(--reel-safe-b))',
              paddingLeft: 'calc(1rem + var(--reel-safe-l))',
              paddingRight: 'calc(1rem + var(--reel-safe-r))',
            }}
            className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-md flex items-center justify-center pointer-events-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              className="bg-reel-chrome-panel backdrop-blur-reel-chrome border border-white/10 shadow-2xl overflow-y-auto"
              // `85%` of the padded backdrop rather than `85vh`: `vh` is the LARGE viewport on iOS
              // Safari, so with the toolbars showing the old cap could exceed the visible height and
              // clip the panel top and bottom with the scroll box trapped inside the clipped part.
              style={{ width: panelWidthCss, maxHeight: '85%', borderRadius: u(panelRadius), padding: u(panelPad) }}
            >
              <h2 className="font-reel-heading font-extrabold text-reel-chrome-textPrimary" style={{ fontSize: type(text.title), marginBottom: u(24) }}>
                {DISCLOSURE.title}
              </h2>
              <p className="font-reel-body text-reel-chrome-textDim leading-relaxed" style={{ fontSize: type(text.rowBody), marginBottom: u(32) }}>
                {DISCLOSURE.intro}
              </p>

              <button
                type="button"
                onClick={onOpenPrivacy}
                className="reel-touch-target relative inline-block uppercase font-reel-body underline decoration-from-font text-reel-chrome-textPrimary hover:opacity-80 transition-opacity"
                style={{ fontSize: type(text.link), marginBottom: u(40) }}
              >
                {DISCLOSURE.privacyLinkLabel}
              </button>

              <div className="flex" style={{ gap: u(geometry.footerBtnGap) }}>
                <button
                  type="button"
                  onClick={() => onSave(rejectAll())}
                  className="reel-touch-target relative flex-1 uppercase bg-reel-cta-primaryBg text-reel-chrome-textInk font-reel-body flex items-center justify-center text-center leading-tight hover:opacity-90 transition-opacity"
                  style={{
                    minHeight: u(geometry.footerH),
                    borderRadius: u(geometry.rowRadius),
                    fontSize: type(text.cta),
                    paddingTop: u(16),
                    paddingBottom: u(16),
                    paddingLeft: u(24),
                    paddingRight: u(24),
                  }}
                >
                  {DISCLOSURE.rejectLabel}
                </button>
                <button
                  type="button"
                  onClick={() => onSave(acceptAll())}
                  className="reel-touch-target relative flex-1 uppercase bg-reel-cta-darkBg text-reel-chrome-textPrimary font-reel-body flex items-center justify-center text-center leading-tight hover:opacity-90 transition-opacity"
                  style={{
                    minHeight: u(geometry.footerH),
                    borderRadius: u(geometry.rowRadius),
                    fontSize: type(text.cta),
                    paddingTop: u(16),
                    paddingBottom: u(16),
                    paddingLeft: u(24),
                    paddingRight: u(24),
                  }}
                >
                  {DISCLOSURE.acceptLabel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="consent-gate"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={DISCLOSURE.title}
          className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-md flex items-center justify-center p-4 pointer-events-auto font-sans text-neutral-900"
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="bg-white/95 backdrop-blur rounded-[2rem] border border-black/10 shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-8"
          >
            <h2 className="text-lg tracking-widest uppercase font-semibold mb-4">{DISCLOSURE.title}</h2>
            <p className="text-sm leading-relaxed text-neutral-600 mb-5">{DISCLOSURE.intro}</p>

            <button
              type="button"
              onClick={onOpenPrivacy}
              className="inline-block text-xs tracking-widest uppercase underline underline-offset-4 text-neutral-800 hover:text-black mb-6"
            >
              {DISCLOSURE.privacyLinkLabel}
            </button>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => onSave(rejectAll())}
                className="flex-1 px-6 py-3 rounded-full border border-black/20 bg-white hover:bg-black/5 transition-colors tracking-widest uppercase text-xs font-medium"
              >
                {DISCLOSURE.rejectLabel}
              </button>
              <button
                type="button"
                onClick={() => onSave(acceptAll())}
                className="flex-1 px-6 py-3 rounded-full border border-black bg-black text-white hover:bg-neutral-800 transition-colors tracking-widest uppercase text-xs font-medium"
              >
                {DISCLOSURE.acceptLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
